// App.js
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdministrationLayout from './layouts/AdministrationLayout';
import AdministrationHome from './pages/AdministrationHome';
import StudentsList from './pages/StudentsList';
import ScolaritePage from './components/ScolaritePage';
// Plus de NotificationProvider à la racine (13/10/2026) : il interrogeait
// /api/notifications/count toutes les cinq secondes, même sans jeton (donc sur
// /connexion, en 401), et personne ne lisait sa valeur. Le compteur de la
// cloche vit une seule fois dans AdministrationLayout (useNotificationCounter).
import ElevesRadies from './pages/ElevesRadies';
import HRManagement from './components/HRManagement';
import ConnexionPage from './components/ConnexionPage';
import ClasseMatieres from './components/ClasseMatieres';
import VieScolairePage from './components/VieScolairePage';
import CantinePage from './components/CantinePage';
import TimetablePage from './components/TimetablePage';
import NotesPage from './components/NotesPage';
import ReInscriptionManager from './components/reinscriptions/ReInscriptionManager';
import GestionComptable from './components/GestionComptable';
import RegistrationManager from './pages/RegistrationManager';
import ExamenPage from './components/ExamenPage';
import ConfigurationParent from './components/ConfigurationParent';
import RealtimeAttendanceDashboard from './components/RealtimeAttendanceDashboard';
import TaskManagement from './components/TaskManagement';

//LIAISON INTER SITE
import InterSiteManagement from './components/InterSiteManagement';

import Support from './components/Support';
// Après les autres imports, ajouter :
import KiosquePointage from './components/KiosquePointage';
import PointageRouter from './components/PointageRouter'; // Nouveau composant de routing
import KiosqueGuard from './components/KiosqueGuard';
import KiosqueWelcomeScreen from './components/KiosqueWelcomeScreen';
// Suivi des enseignants (lot D) : la vue globale de la direction et la page
// de chaque enseignant. Remplace « Suivi direction » (SuiviEnseignantsDirection,
// laissé mort) : la direction suit et commente les programmes, elle ne les
// valide plus.
import SuiviEnseignants from './components/suiviEnseignants/SuiviEnseignants';
import PageEnseignantSuivi from './components/suiviEnseignants/PageEnseignantSuivi';
// Notes saisies par les enseignants (espace enseignant, N1) : la page de
// validation d'une feuille, atteinte depuis le carnet, le suivi ou la cloche ;
// au primaire (N3), la page du mois d'une classe, où la direction valide le
// mois en un geste.
import PageFeuilleNotes from './components/validationNotes/PageFeuilleNotes';
import PageMoisPrimaire from './components/validationNotes/PageMoisPrimaire';
import GardeValidationNotes from './components/validationNotes/GardeValidationNotes';


// Import du centre d'administration
import AdminCenter from './pages/admin/AdminCenter';

// Imports pour le système de permissions
import { PermissionsProvider } from './hooks/usePermissions';
import ProtectedRoute from './components/ProtectedRoute';
import { ParametresProvider } from './contexts/ParametresContext';
import { CaisseProvider } from './contexts/CaisseContext';
import { FeaturesProvider } from './contexts/FeaturesContext';

// ----------------------------------------------------------------------------
// ESPACE FAMILLE — la version parents de KLASYS, sur /espace-famille/*
// ----------------------------------------------------------------------------
// Chargé à la demande : l'administration n'en télécharge rien. Il a sa propre
// session (jeton famille), indépendante de `isLoggedInNew`. Ouvert école par
// école par la licence `module.parents` : fermée, la page le dit et le serveur
// refuse toute requête. Remplace l'aperçu statique de 2024 (HomePage,
// LoginPage, InscriptionParent), qui n'est plus branché.
const EspaceFamilleApp = lazy(() => import('./espace-famille/EspaceFamilleApp'));
const MessagesFamilles = lazy(() => import('./components/accesFamille/MessagesFamilles'));
const chargementEspaceFamille = <div className="min-h-screen bg-[#eaf4f7]" />;

// Une page d'administration chargee a la demande : un repli discret plutot
// que `null`. C'etait le seul `fallback={null}` du fichier, et pendant le
// telechargement du morceau de code l'ecran restait blanc sans un mot.
const chargementPageAdministration = (
  <div role="status" className="flex h-64 items-center justify-center text-sm text-gray-500">
    <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#246990]/30 border-t-[#246990]" />
    <span className="ml-3">Chargement...</span>
  </div>
);

// ----------------------------------------------------------------------------
// ESPACE ENSEIGNANT — la version enseignants de KLASYS, sur /espace-enseignant/*
// ----------------------------------------------------------------------------
// Même principe que l'espace famille : chargé à la demande, sa propre session
// (jeton enseignant, jamais la clé `token` du personnel), ouvert école par
// école par la licence `module.portail_enseignant`. L'ancien portail tablette
// (/portail-enseignant, PortailEnseignant) est retiré : voir `VersEspaceEnseignant`.
const EspaceEnseignantApp = lazy(() => import('./espace-enseignant/EspaceEnseignantApp'));
const chargementEspaceEnseignant = <div className="min-h-screen bg-[#fbf1ee]" />;

// Une page chargée sur /espace-famille ou /espace-enseignant se rend SEULE,
// hors des fournisseurs de l'administration (permissions, licence, paramètres,
// caisse ; les notifications en faisaient partie jusqu'au 13/10/2026). Ceux-ci
// appellent /api dès leur montage avec le jeton `token` et le cookie
// de session du personnel. Sur un poste partagé où la secrétaire est restée
// connectée, la page du parent ou de l'enseignant interrogeait donc
// l'administration EN SON NOM, et chaque appel marquait sa session active
// (`lastActivity`, SessionService) : pour le serveur, la secrétaire
// travaillait encore. Les deux espaces n'utilisent aucun de ces fournisseurs :
// ils ont leur session, leurs notifications et leurs toasts.
//
// Toutes les sorties des deux espaces sont des liens pleins (`<a href>`,
// `window.location`) : la page suivante recharge l'application et retrouve
// l'arbre complet. Les routes restent aussi déclarées plus bas, dans l'arbre
// de l'administration, pour le cas où l'on y arriverait sans recharger.
const ADRESSE_ESPACE_FAMILLE = /^\/espace-famille(\/|$)/i;
const EspaceFamilleSeul = () => (
  <BrowserRouter>
    <Routes>
      <Route
        path="/espace-famille/*"
        element={<Suspense fallback={chargementEspaceFamille}><EspaceFamilleApp /></Suspense>}
      />
      <Route path="*" element={<Navigate to="/espace-famille" replace />} />
    </Routes>
  </BrowserRouter>
);

const ADRESSE_ESPACE_ENSEIGNANT = /^\/espace-enseignant(\/|$)/i;
const EspaceEnseignantSeul = () => (
  <BrowserRouter>
    <Routes>
      <Route
        path="/espace-enseignant/*"
        element={<Suspense fallback={chargementEspaceEnseignant}><EspaceEnseignantApp /></Suspense>}
      />
      <Route path="*" element={<Navigate to="/espace-enseignant" replace />} />
    </Routes>
  </BrowserRouter>
);

// L'ancien portail tablette (/portail-enseignant) est retiré côté enseignant :
// ses écritures détruiraient les données de l'espace enseignant (programme
// supprimé puis recréé à chaque enregistrement, cours remis à « préparé » et
// élèves effacés à chaque appel), et il n'a jamais servi en production. Son
// adresse (favori, lien gardé) mène à l'espace enseignant. Chargée telle
// quelle, elle passe par `EspaceEnseignantSeul`, dont la route par défaut
// renvoie sur /espace-enseignant sans monter les fournisseurs de
// l'administration ; atteinte sans recharger, depuis l'arbre de
// l'administration, `VersEspaceEnseignant` recharge pour la même raison.
const ADRESSE_ANCIEN_PORTAIL = /^\/portail-enseignant(\/|$)/i;
const VersEspaceEnseignant = () => {
  useEffect(() => {
    window.location.replace('/espace-enseignant');
  }, []);
  return chargementEspaceEnseignant;
};

function App() {
  const adresse = window.location.pathname;
  if (ADRESSE_ESPACE_FAMILLE.test(adresse)) return <EspaceFamilleSeul />;
  if (ADRESSE_ESPACE_ENSEIGNANT.test(adresse) || ADRESSE_ANCIEN_PORTAIL.test(adresse)) {
    return <EspaceEnseignantSeul />;
  }

  // Gestion de l'état de connexion pour le nouveau système uniquement
  const isLoggedInNew = localStorage.getItem('isLoggedInNew') === 'true';
  const storedRedirect = localStorage.getItem('redirectTo') || '/administration';
  // `/portail-enseignant` : la destination que le serveur donnait à un
  // « enseignant seul » avant le retrait de l'ancien portail, encore gardée
  // par les sessions du personnel ouvertes avant lui. Suivie, elle enverrait
  // la racine, /connexion et toute adresse inconnue vers l'espace enseignant,
  // où cette session ne vaut rien : sur un poste partagé, la page de connexion
  // du personnel ne s'atteindrait plus. Elle prend la destination que le
  // serveur donne aujourd'hui, l'administration.
  const postLoginRedirect = storedRedirect === '/admin' || storedRedirect === '/portail-enseignant'
    ? '/administration'
    : storedRedirect;
  return (
      <PermissionsProvider>
        <FeaturesProvider>
        <ParametresProvider>
        <CaisseProvider>
          <BrowserRouter>
            <Routes>
              {/* Route racine - Redirection vers le nouveau système */}
              <Route
                path="/"
                element={<Navigate to={isLoggedInNew ? postLoginRedirect : "/connexion"} />}
              />

              {/* Page de connexion */}

              <Route
                path="/connexion"
                element={isLoggedInNew ? <Navigate to={postLoginRedirect} /> : <ConnexionPage />}
              />

              {/* ✅ NOUVEAU : Route Welcome Kiosque - Animation avant redirection */}
              <Route
                path="/kiosque-welcome"
                element={isLoggedInNew ? <KiosqueWelcomeScreen /> : <Navigate to="/connexion" />}
              />

              {/* Route Kiosque RFID - plein ecran, hors administration.
                  N'exigeait qu'une session : n'importe quel employe connecte
                  l'atteignait, meme dans une ecole qui n'a pas achete le
                  pointage, et la permission POINTAGES.KIOSK.VIEW - qui existe et
                  s'affiche dans le gestionnaire de roles - ne commandait rien.
                  Le kiosque est le seul usage des badges : sa licence est
                  `cap.rfid`, l'extension dont il depend. */}
              <Route
                path="/kiosque-pointage"
                element={isLoggedInNew ? (
                  <KiosqueGuard>
                    <KiosquePointage />
                  </KiosqueGuard>
                ) : <Navigate to="/connexion" />}
              />

              {/* Ancien portail tablette, retiré : son adresse mène à l'espace enseignant */}
              <Route path="/portail-enseignant" element={<VersEspaceEnseignant />} />
              {/* Routes protégées - Nouveau système avec gestion des permissions */}
              <Route
                path="/administration"
                element={isLoggedInNew ? <AdministrationLayout /> : <Navigate to="/connexion" />}
              >
                <Route index element={<AdministrationHome />} />

                {/* Routes Élèves protégées */}
                <Route path="dossier-eleves">
                  <Route path="inscription" element={
                    <ProtectedRoute requiredModule="ELEVES">
                      <RegistrationManager />
                    </ProtectedRoute>
                  } />
                  <Route path="liste" element={
                    <ProtectedRoute requiredModule="ELEVES">
                      <StudentsList />
                    </ProtectedRoute>
                  } />
                  <Route path="radies" element={
                    <ProtectedRoute requiredModule="ELEVES">
                      <ElevesRadies />
                    </ProtectedRoute>
                  } />
                </Route>

                {/* Route Emploi du temps protégée */}
                {/* Sous la permission ELEVES, mais vendus separement : la cle de
                    licence est donc precisee explicitement. */}
                <Route path="emploi-du-temps" element={
                  <ProtectedRoute requiredModule="ELEVES" requiredFeature="module.emploi_du_temps">
                    <TimetablePage />
                  </ProtectedRoute>
                } />


                {/* Messages des familles (espace famille) : la licence module.parents
                    ferme la page ; le droit de répondre est vérifié par le serveur. */}
                <Route path="messages-familles" element={
                  <ProtectedRoute requiredModule="ELEVES" requiredFeature="module.parents">
                    <Suspense fallback={chargementPageAdministration}><MessagesFamilles /></Suspense>
                  </ProtectedRoute>
                } />

                {/* Route Disciplinaire (Vie scolaire) protégée */}
                <Route path="disciplinaire" element={
                  <ProtectedRoute requiredModule="ELEVES" requiredFeature="module.vie_scolaire">
                    <VieScolairePage />
                  </ProtectedRoute>
                } />

                {/* Cantine scolaire protégée */}
                <Route path="cantine" element={
                  <ProtectedRoute requiredModule="CANTINE">
                    <CantinePage />
                  </ProtectedRoute>
                } />

                {/* Suivi des enseignants : la porte SUIVI_ENSEIGNANTS et la licence
                    module.suivi_enseignants (LICENCE_PAR_MODULE). La permission
                    fine SUIVI_ENSEIGNANTS.VUE_GLOBALE.VIEW est vérifiée par le
                    serveur, et par le menu pour ne pas montrer l'entrée sans elle. */}
                <Route path="enseignants" element={
                  <ProtectedRoute requiredModule="SUIVI_ENSEIGNANTS">
                    <SuiviEnseignants />
                  </ProtectedRoute>
                } />
                <Route path="enseignants/:employeId" element={
                  <ProtectedRoute requiredModule="SUIVI_ENSEIGNANTS">
                    <PageEnseignantSuivi />
                  </ProtectedRoute>
                } />
                {/* Ressources Humaines protégées */}
                <Route path="ressources-humaines" element={
                  <ProtectedRoute requiredModule="RESSOURCE_HUMAINES">
                    <HRManagement />
                  </ProtectedRoute>
                } />

                {/* Pointage - Redirection intelligente selon permissions */}
                <Route path="pointage" element={
                  <ProtectedRoute requiredModule="POINTAGES">
                    <PointageRouter />
                  </ProtectedRoute>
                } />

                {/* Centre de support : la porte ADMINISTRATION (13/10/2026).
                    Il était gardé par RESSOURCE_HUMAINES, que ses routes serveur
                    n'exigent pas (ADMINISTRATION.SUPPORT.*) : la porte ouvrait
                    l'écran à qui ne pouvait rien y lire, et le fermait à des
                    titulaires de ces droits sans la porte RH. ADMINISTRATION
                    relève du socle (pas de clé de licence). Relevé du 13/10 sur
                    les six écoles actives : aucun titulaire de SUPPORT.VIEW ou
                    .VALIDER ne perd l'écran. La lecture fine (SUPPORT.VIEW) est
                    vérifiée dans Support.jsx, la décision par le serveur. */}
                <Route path="support" element={
                  <ProtectedRoute requiredModule="ADMINISTRATION">
                    <Support />
                  </ProtectedRoute>
                } />
                <Route path="support/requests/:requestId" element={
                  <ProtectedRoute requiredModule="ADMINISTRATION">
                    <Support />
                  </ProtectedRoute>
                } />

                {/* Scolarité */}
                <Route path="scolarite" element={
                  <ProtectedRoute requiredModule="CLASSE_ET_MATIERES">
                    <ScolaritePage />
                  </ProtectedRoute>
                } />

                {/* Classes et Matières protégées */}
                <Route path="classe-et-matieres" element={
                  <ProtectedRoute requiredModule="CLASSE_ET_MATIERES">
                    <ClasseMatieres />
                  </ProtectedRoute>
                } />

                {/* Carnet d'évaluations (Notes) protégé */}
                <Route path="notes" element={
                  <ProtectedRoute requiredModule="CARNET_EVALUATION">
                    <NotesPage />
                  </ProtectedRoute>
                } />

                {/* La page d'une feuille de notes d'enseignant. Pas de
                    ProtectedRoute : la porte du carnet n'est pas celle de ces
                    données (on y vient aussi du suivi et de la cloche) ; la garde
                    exige les deux licences — espace enseignant ET carnet — et une
                    clé de lecture d'un des deux niveaux (NOTES.VALIDATION.* pour
                    le collège et le lycée, NOTES.VALIDATION_PRIMAIRE.* pour le
                    primaire). Le serveur revérifie tout, niveau compris. */}
                <Route path="notes/saisies/:feuilleId" element={
                  <GardeValidationNotes>
                    <PageFeuilleNotes />
                  </GardeValidationNotes>
                } />

                {/* Le mois d'une classe du primaire (N3) : mêmes licences, clés
                    du primaire seulement, sans repli sur celles du collège. */}
                <Route path="notes/saisies/primaire/:classeId/:moisId" element={
                  <GardeValidationNotes niveau="primaire">
                    <PageMoisPrimaire />
                  </GardeValidationNotes>
                } />

                {/* Réinscriptions */}
                <Route path="reinscriptions" element={
                  <ProtectedRoute requiredModule="ELEVES">
                    <ReInscriptionManager />
                  </ProtectedRoute>
                } />

                {/* Comptabilité et Paie protégées */}
                <Route path="comptabilite" element={
                  <ProtectedRoute requiredModule="COMPTABILITE_ET_PAIE">
                    <GestionComptable />
                  </ProtectedRoute>
                } />

                <Route path="recouvrement" element={
                  <ProtectedRoute requiredModule="COMPTABILITE_ET_PAIE">
                    <Navigate to="/administration/comptabilite?tab=recouvrement" replace />
                  </ProtectedRoute>
                } />

                {/* Tâches Inter-Services.
                    Le module s'appelle TACHES en base (22 permissions).
                    TACHES_INTER_SITE n'existe nulle part : la route n'était
                    donc atteignable que par les porteurs d'ALL_MODULES. */}
                <Route path="taches" element={
                  <ProtectedRoute requiredModule="TACHES">
                    <TaskManagement />
                  </ProtectedRoute>
                } />

                {/* Liaison Inter-Sites */}
                <Route path="intersite" element={
                  <ProtectedRoute requiredModule="LIAISON_INTER_SITES">
                    <InterSiteManagement />
                  </ProtectedRoute>
                } />

                {/* Examens protégés */}
                <Route path="examens" element={
                  <ProtectedRoute requiredModule="INSCRIPTION_AUX_EXAMENS">
                    <ExamenPage />
                  </ProtectedRoute>
                } />

                {/* Configuration (Outils administratifs) protégée */}
                <Route path="configuration" element={
                  <ProtectedRoute requiredModule="OUTILS_ADMINISTRATIFS">
                    <ConfigurationParent />
                  </ProtectedRoute>
                } />

                {/* Affluence (Tendances et analyses) protégée */}
                <Route path="affluence" element={
                  <ProtectedRoute requiredModule="TENDANCES_ET_ANALYSE">
                    <RealtimeAttendanceDashboard />
                  </ProtectedRoute>
                } />

                {/* CENTRE D'ADMINISTRATION - Accès super admin uniquement */}
                <Route path="admin" element={
                  <ProtectedRoute requiredModule="ADMIN_CENTER" requiredPermission="SUPER_ADMIN">
                    <AdminCenter />
                  </ProtectedRoute>
                } />
              </Route>

              {/* Page d'accès refusé */}
              <Route path="/access-denied" element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="max-w-md w-full text-center">
                    <div className="text-red-500 text-6xl mb-4">??</div>
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">Accès Refusé</h1>
                    <p className="text-gray-600 mb-6">
                      Vous n'avez pas les permissions nécessaires pour accéder à cette page.
                    </p>
                    <button
                      onClick={() => window.history.back()}
                      className="bg-[#246990] text-white px-6 py-2 rounded-lg hover:bg-[#3084b5] transition-colors"
                    >
                      Retour
                    </button>
                  </div>
                </div>
              } />

              {/* Espace famille (parents) : sa propre session, sa propre coquille */}
              <Route
                path="/espace-famille/*"
                element={<Suspense fallback={chargementEspaceFamille}><EspaceFamilleApp /></Suspense>}
              />

              {/* Espace enseignant : sa propre session, sa propre coquille */}
              <Route
                path="/espace-enseignant/*"
                element={<Suspense fallback={chargementEspaceEnseignant}><EspaceEnseignantApp /></Suspense>}
              />

              {/* Route pour les URLs non trouvées */}
              <Route path="*" element={<Navigate to={isLoggedInNew ? postLoginRedirect : "/connexion"} />} />
            </Routes>
          </BrowserRouter>
        </CaisseProvider>
        </ParametresProvider>
        </FeaturesProvider>
      </PermissionsProvider>
  );
}

export default App;
